import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import heroImage from "@/assets/hero-river-families.webp";
import heroVideo from "@/assets/sons-blue-river-hero-720p.mp4.asset.json";
import { Button } from "@/components/ui/button";
import { gtagReportConversion } from "@/lib/gtag";

const HeroSection = () => {
  const [showVideo, setShowVideo] = useState(false);

  useEffect(() => {
    // Defer YouTube iframe until after the LCP image has painted
    const load = () => {
      const t = setTimeout(() => setShowVideo(true), 1500);
      return () => clearTimeout(t);
    };
    if (document.readyState === "complete") {
      return load();
    }
    window.addEventListener("load", load, { once: true });
    return () => window.removeEventListener("load", load);
  }, []);

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Background Video/Image */}
      <div className="absolute inset-0">
        <div className="relative w-full h-full overflow-hidden">
          {/* LCP image — paints immediately */}
          <img
            src={heroImage}
            alt="Families enjoying the San Marcos River at Son's Blue River Camp"
            className="absolute inset-0 w-full h-full object-cover"
            fetchPriority="high"
            decoding="async"
            width={1920}
            height={1080}
          />
          {showVideo && (
            <video
              src={heroVideo.url}
              autoPlay
              muted
              loop
              playsInline
              preload="none"
              aria-label="Son's Blue River Camp Experience"
              className="absolute inset-0 w-full h-full object-cover pointer-events-none"
            />
          )}
        </div>
      </div>

      {/* Content - Centered */}
      <div className="relative container mx-auto px-4 text-center flex flex-col items-center justify-center">
        {/* Headline */}
        <h1 className="relative font-heading text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-cream leading-tight mb-6 animate-fade-in-up [text-shadow:_-1px_-1px_0_#000,_1px_-1px_0_#000,_-1px_1px_0_#000,_1px_1px_0_#000,_0_4px_12px_rgba(0,0,0,0.8)] lg:[text-shadow:_-1px_-1px_0_#000,_1px_-1px_0_#000,_-1px_1px_0_#000,_1px_1px_0_#000,_0_4px_8px_rgba(0,0,0,0.7)]">
          <span className="relative">
            <span className="absolute inset-0 bg-black/30 blur-xl rounded-xl lg:hidden" />
            <span className="relative">San Marcos River Glamping, Tubing & Cabins in{" "}</span>
          </span>
          <span className="relative text-[#3d8a8a] [text-shadow:_-1px_-1px_0_#000,_1px_-1px_0_#000,_-1px_1px_0_#000,_1px_1px_0_#000,_0_4px_12px_rgba(0,0,0,0.8)] lg:[text-shadow:_-1px_-1px_0_#000,_1px_-1px_0_#000,_-1px_1px_0_#000,_1px_1px_0_#000,_0_4px_8px_rgba(0,0,0,0.7)]">
            <span className="absolute inset-0 bg-black/30 blur-xl rounded-xl lg:hidden" />
            <span className="relative">Kingsbury, TX</span>
          </span>
        </h1>


        {/* Book Button - All devices */}
        <div className="animate-fade-in-up opacity-0 stagger-3">
          <Button asChild size="lg" className="text-lg px-10 py-7 bg-copper hover:bg-copper-dark text-cream shadow-elevated">
            <Link to="/book-now#sons-blue-river-camp">Book Your River Getaway</Link>
          </Button>
        </div>

        {/* Navigation Buttons - All devices */}
        <div className="flex flex-col sm:flex-row gap-3 mt-6 animate-fade-in-up opacity-0 stagger-3">
          <Button
            asChild
            size="default"
            className="bg-transparent border-2 border-cream/70 text-cream hover:bg-cream/10 font-semibold px-5 py-3 [text-shadow:_0_2px_12px_rgba(0,0,0,1),_0_4px_20px_rgba(0,0,0,0.9)]"
          >
            <Link to="/book-now?plan=overnight" onClick={() => gtagReportConversion()}>Reserve a Cabin</Link>
          </Button>
          <Button
            asChild
            size="default"
            className="bg-transparent border-2 border-cream/70 text-cream hover:bg-cream/10 font-semibold px-5 py-3 [text-shadow:_0_2px_12px_rgba(0,0,0,1),_0_4px_20px_rgba(0,0,0,0.9)]"
          >
            <Link to="/book-now?plan=day" onClick={() => gtagReportConversion()}>Reserve a Riverside Cabana</Link>
          </Button>
          <Button
            asChild
            size="default"
            className="bg-transparent border-2 border-cream/70 text-cream hover:bg-cream/10 font-semibold px-5 py-3 [text-shadow:_0_2px_12px_rgba(0,0,0,1),_0_4px_20px_rgba(0,0,0,0.9)]"
          >
            <Link to="/book-now?plan=tubing" onClick={() => gtagReportConversion()}>Go Tubing & Kayaking</Link>
          </Button>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-float">
        <div className="w-8 h-12 rounded-full border-2 border-cream/30 flex items-start justify-center p-2">
          <div className="w-1.5 h-3 rounded-full bg-cream/60 animate-pulse" />
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
